import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Stat, StatusBadge } from "@/components/app/ui-bits";
import {
  Warning, Siren, Calendar, Coins, ShieldWarning, ClipboardText,
  CalendarCheck, Package, Wrench, AirplaneTilt, Pulse,
} from "@phosphor-icons/react";
import { formatCurrency, formatDate } from "@/lib/api";
import { Link, Navigate } from "react-router-dom";
import CommandCenter from "@/components/app/CommandCenter";
import PortfolioRiskRadar from "@/components/app/PortfolioRiskRadar";
import PortfolioHeatmap from "@/components/app/PortfolioHeatmap";
import { useAuth } from "@/contexts/AuthContext";
import {
  isFinance, isHR, isProcurement, isProjectManager, isCompliance,
  isAssetManager, isExecutive,
} from "@/lib/roles";
import {
  FinanceDashboard, HRDashboard, ProcurementDashboard, PMDashboard,
  AssetMgrDashboard, ComplianceDashboard, RoleDashboardSwitcher,
} from "@/pages/RoleDashboards";

const ROLE_VIEWS = {
  finance:    { label: "Finance lens", show: ["finance", "alerts", "metrics", "deliverables"] },
  hr:         { label: "HR lens", show: ["metrics", "alerts", "deliverables"] },
  manager:    { label: "PM lens", show: ["radar", "metrics", "alerts", "deliverables", "appointments"] },
  compliance: { label: "Compliance lens", show: ["alerts", "metrics", "deliverables"] },
  admin:      { label: "Admin (all)", show: ["radar", "metrics", "alerts", "finance", "deliverables", "appointments", "assets", "incidents"] },
  super_admin:{ label: "Super-admin (all)", show: ["radar", "metrics", "alerts", "finance", "deliverables", "appointments", "assets", "incidents"] },
};
const ROLE_LENS_CHOICES = ["auto", "admin", "manager", "finance", "hr", "compliance"];

export default function Dashboard() {
  const { user } = useAuth();

  // ── ALL HOOKS DECLARED FIRST (React rules) ─────────────────────────
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  // Lens lets admins preview other role-views; non-admins default to their dept_scope lens.
  const [lens, setLens] = useState("auto");

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: p }] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/dashboard/finance-pulse"),
        ]);
        setData(data);
        setPulse(p);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Role-aware landing (AFTER hooks) ────────────────────────────────
  // Employees → personal portal; each specialist role → tailored dashboard.
  // Only CEO / Super Admin / Admin / Management see the legacy Command Center.
  const isEmployee = user && user.role === "staff" && !user.dept_scope;
  if (isEmployee) return <Navigate to="/me" replace />;

  if (user && !isExecutive(user)) {
    if (isFinance(user))         return <RoleDashboardSwitcher>{({ counters }) => <FinanceDashboard counters={counters} />}</RoleDashboardSwitcher>;
    if (isHR(user))              return <RoleDashboardSwitcher>{({ counters }) => <HRDashboard counters={counters} />}</RoleDashboardSwitcher>;
    if (isProcurement(user))     return <RoleDashboardSwitcher>{({ counters }) => <ProcurementDashboard counters={counters} />}</RoleDashboardSwitcher>;
    if (isProjectManager(user))  return <RoleDashboardSwitcher>{({ counters }) => <PMDashboard counters={counters} />}</RoleDashboardSwitcher>;
    if (isAssetManager(user))    return <RoleDashboardSwitcher>{({ counters }) => <AssetMgrDashboard counters={counters} />}</RoleDashboardSwitcher>;
    if (isCompliance(user))      return <RoleDashboardSwitcher>{({ counters }) => <ComplianceDashboard counters={counters} />}</RoleDashboardSwitcher>;
  }

  // Legacy executive Command Center (CEO / Super Admin / Admin / Management)
  // Derive the effective lens from dept_scope first (Finance officer's scope is "finance"
  // even though their role is "manager"), falling back to the base role.
  const scopeLensMap = { finance: "finance", hr: "hr", compliance: "compliance", pm: "manager" };
  const derivedFromScope = scopeLensMap[user?.dept_scope || ""] || null;
  const effectiveRole = lens === "auto"
    ? (derivedFromScope || user?.role || "admin")
    : lens;
  const view = ROLE_VIEWS[effectiveRole] || ROLE_VIEWS.admin;
  const show = (k) => view.show.includes(k);

  if (loading) return <div className="p-10 font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading dashboard…</div>;
  if (!data) return null;

  const {
    counts, alerts, finance, upcoming_deliverables,
    upcoming_appointments, calibration_due_soon, assets_deployed,
    open_incidents, pending_mobilisations,
  } = data;

  const hasAlerts = (alerts.expired_compliance?.length || 0) + (alerts.overdue_tasks?.length || 0) > 0;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Warnings, alerts, priorities and key updates across the organisation."
      />

      <div className="space-y-5 p-5 md:space-y-6 md:p-8">
        {/* Role lens (admins can preview other roles' dashboards) */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-mono-alt uppercase tracking-widest text-slate-500">Viewing as:</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-900" data-testid="dash-lens-current">
            {view.label}
          </span>
          {(user?.role === "admin" || user?.role === "super_admin") && (
            <div className="flex items-center gap-1 ml-2">
              {ROLE_LENS_CHOICES.map((r) => (
                <button key={r} onClick={() => setLens(r)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-mono-alt uppercase tracking-wider transition ${lens === r ? "bg-[#0f1e42] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}
                  data-testid={`dash-lens-${r}`}>
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Executive Command Center — operational pulse */}
        <CommandCenter />

        {/* Portfolio Profitability Heatmap — executive financial overview (silently hidden for non-finance users via 403) */}
        <PortfolioHeatmap />

        {/* Portfolio Risk Radar — PM / Admin */}
        {show("radar") && <PortfolioRiskRadar />}

        {/* Key metrics — grid collapses nicely on mobile */}
        {show("metrics") && (
        <section className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 lg:grid-cols-7">
          <Stat label="Projects" value={counts.active_projects} testId="stat-active-projects" />
          <Stat label="Staff" value={counts.active_staff} testId="stat-active-staff" />
          <Stat label="Open Tasks" value={counts.open_tasks} testId="stat-open-tasks" />
          <Stat label="Pending Review" value={counts.pending_review} testId="stat-pending-review" />
          <Stat label="Assets" value={counts.assets_total} testId="stat-assets" />
          <Stat label="New Appts" value={counts.appointments_new} testId="stat-appts-new" />
          <Stat label="Users" value={counts.users} testId="stat-users" />
        </section>
        )}

        {/* Alerts + Finance */}
        {(show("alerts") || show("finance")) && (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {show("alerts") && (
          <div className="dc-card p-5 lg:col-span-2" data-testid="alerts-card">
            <CardHeader
              icon={<Siren size={16} weight="fill" className="text-red-600" />}
              title="Warnings & Alerts"
              meta={hasAlerts
                ? `${(alerts.expired_compliance?.length || 0) + (alerts.overdue_tasks?.length || 0)} active`
                : "all clear"}
            />
            {!hasAlerts && (alerts.expiring_compliance || []).length === 0 ? (
              <Empty>No warnings. Everything is in order.</Empty>
            ) : (
              <ul className="mt-3 divide-y divide-slate-100">
                {(alerts.expired_compliance || []).map((c) => (
                  <li key={c.id} className="flex items-start gap-3 py-3" data-testid={`alert-expired-${c.id}`}>
                    <ShieldWarning size={18} className="mt-0.5 text-red-600" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">EXPIRED: {c.title}</div>
                      <div className="text-xs text-slate-500">{c.category?.replace("_", " ")} · expired {formatDate(c.expiry_date)}</div>
                    </div>
                    <StatusBadge status="rejected" />
                  </li>
                ))}
                {(alerts.expiring_compliance || []).map((c) => (
                  <li key={c.id} className="flex items-start gap-3 py-3" data-testid={`alert-expiring-${c.id}`}>
                    <Warning size={18} className="mt-0.5 text-amber-600" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">{c.title}</div>
                      <div className="text-xs text-slate-500">{c.category?.replace("_", " ")} · expires {formatDate(c.expiry_date)}</div>
                    </div>
                    <StatusBadge status="pending" />
                  </li>
                ))}
                {(alerts.overdue_tasks || []).map((t) => (
                  <li key={t.id} className="flex items-start gap-3 py-3" data-testid={`alert-overdue-${t.id}`}>
                    <ClipboardText size={18} className="mt-0.5 text-red-600" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">Overdue: {t.title}</div>
                      <div className="text-xs text-slate-500">Due {formatDate(t.due_date)}</div>
                    </div>
                    <StatusBadge status="rejected" />
                  </li>
                ))}
              </ul>
            )}
          </div>
          )}

          {show("finance") && (
          <div className="dc-card p-5" data-testid="finance-snapshot">
            <CardHeader
              icon={<Coins size={16} weight="fill" style={{ color: "var(--descon-accent, #1E5CBA)" }} />}
              title="Finance Snapshot"
              action={<Link to="/finance" className="dc-card-link">open →</Link>}
            />
            <dl className="mt-4 space-y-3 text-sm">
              <Row label="Total revenue" value={formatCurrency(finance.total_revenue)} />
              <Row label="Project costs" value={formatCurrency(finance.total_project_cost)} />
              <Row label="Overhead" value={formatCurrency(finance.total_overhead)} />
              <div className="flex items-baseline justify-between border-t border-slate-100 pt-3">
                <dt className="font-semibold text-slate-900">Net</dt>
                <dd className={`font-mono-alt font-semibold tabular-nums ${finance.net >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {formatCurrency(finance.net)}
                </dd>
              </div>
            </dl>
          </div>
          )}
        </section>
        )}

        {/* Finance Health Pulse — projected close · overdue donors · unreconciled banks */}
        {show("finance") && pulse && <FinancePulseCard pulse={pulse} />}

        {/* Appointments + Calibration */}
        {show("appointments") && (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="dc-card" data-testid="appointments-card">
            <CardHeader
              icon={<CalendarCheck size={16} weight="fill" style={{ color: "#1E5CBA" }} />}
              title="Upcoming Appointments"
              meta={`${counts.appointments_pending} pending`}
              action={<Link to="/appointments" className="dc-card-link">manage →</Link>}
              inset
            />
            {upcoming_appointments?.length ? (
              <ul className="divide-y divide-slate-100">
                {upcoming_appointments.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 px-5 py-3">
                    <div className="font-mono-alt w-20 text-xs text-slate-500">{formatDate(a.preferred_date)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{a.full_name}{a.company ? ` · ${a.company}` : ""}</div>
                      <div className="truncate text-xs text-slate-500">{a.purpose}{a.preferred_time ? ` · ${a.preferred_time}` : ""}</div>
                    </div>
                    <StatusBadge status={a.status === "new" ? "pending" : a.status === "confirmed" ? "active" : a.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No upcoming appointments yet.</Empty>
            )}
          </div>

          <div className="dc-card" data-testid="calibration-card">
            <CardHeader
              icon={<Wrench size={16} weight="fill" style={{ color: "#b8860b" }} />}
              title="Assets Due for Calibration"
              meta={`next 30 days`}
              action={<Link to="/assets" className="dc-card-link">register →</Link>}
              inset
            />
            {calibration_due_soon?.length ? (
              <ul className="divide-y divide-slate-100">
                {calibration_due_soon.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 px-5 py-3">
                    <div className="font-mono-alt w-28 text-xs text-slate-500">{formatDate(a.calibration_due)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{a.asset_id}</div>
                      <div className="truncate text-xs text-slate-500">{a.description}</div>
                    </div>
                    <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{a.category}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No calibration due within 30 days.</Empty>
            )}
          </div>
        </section>
        )}

        {/* Assets Deployed + Incidents + Mobilisation */}
        {(show("assets") || show("incidents")) && (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="dc-card" data-testid="deployed-card">
            <CardHeader
              icon={<AirplaneTilt size={16} weight="fill" style={{ color: "#1E5CBA" }} />}
              title="Assets Currently Deployed"
              inset
            />
            {assets_deployed?.length ? (
              <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {assets_deployed.map((a) => (
                  <li key={a.id} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-alt text-xs text-slate-900">{a.asset_id}</span>
                      <StatusBadge status={a.status === "deployed" ? "active" : "pending"} />
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{a.description}</div>
                    {a.location && <div className="mt-0.5 truncate text-[11px] text-slate-400">→ {a.location}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No assets currently in the field.</Empty>
            )}
          </div>

          <div className="dc-card" data-testid="incidents-card">
            <CardHeader
              icon={<Siren size={16} weight="fill" className="text-red-600" />}
              title="Open Incident Reports"
              meta={`${open_incidents?.length || 0} open`}
              inset
            />
            {open_incidents?.length ? (
              <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {open_incidents.map((i) => (
                  <li key={i.id} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-alt text-xs text-slate-900">{i.asset_id}</span>
                      <span className="font-mono-alt text-[10px] uppercase tracking-widest text-red-600">{i.nature}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{i.details}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No open incidents.</Empty>
            )}
          </div>

          <div className="dc-card" data-testid="mobilisation-card">
            <CardHeader
              icon={<Pulse size={16} weight="fill" style={{ color: "#1E5CBA" }} />}
              title="Pending Mobilisations"
              meta={`${pending_mobilisations?.length || 0} queued`}
              inset
            />
            {pending_mobilisations?.length ? (
              <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {pending_mobilisations.map((m) => (
                  <li key={m.id} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">{m.destination}</span>
                      <span className="font-mono-alt text-[10px] text-slate-500">{formatDate(m.deployment_date)}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">PM: {m.project_manager}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No pending mobilisation requests.</Empty>
            )}
          </div>
        </section>
        )}

        {/* Upcoming deliverables */}
        {show("deliverables") && (
        <section className="grid grid-cols-1 gap-4">
          <div className="dc-card" data-testid="priorities-card">
            <CardHeader
              icon={<Calendar size={16} weight="fill" className="text-slate-700" />}
              title="Upcoming Deliverables"
              action={<Link to="/projects" className="dc-card-link">all projects →</Link>}
              inset
            />
            {upcoming_deliverables?.length ? (
              <ul className="divide-y divide-slate-100">
                {upcoming_deliverables.map((d) => (
                  <li key={`${d.project_id}-${d.deliverable_id}`} className="flex items-center gap-3 px-5 py-3">
                    <div className="font-mono-alt w-24 text-xs text-slate-500">{formatDate(d.due_date)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{d.title}</div>
                      <div className="truncate text-xs text-slate-500">{d.project_name}</div>
                    </div>
                    <StatusBadge status={d.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No upcoming deliverables.</Empty>
            )}
          </div>
        </section>
        )}
      </div>
    </div>
  );
}

const CardHeader = ({ icon, title, meta, action, inset }) => (
  <div className={`flex items-center justify-between gap-3 border-b border-slate-100 ${inset ? "px-5 py-3" : "pb-3"}`}>
    <div className="flex items-center gap-2 min-w-0">
      <span className="flex-shrink-0">{icon}</span>
      <h3 className="truncate font-display text-[15px] font-semibold text-slate-900">{title}</h3>
    </div>
    <div className="flex flex-shrink-0 items-center gap-3">
      {meta && (
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{meta}</span>
      )}
      {action}
    </div>
  </div>
);

const Empty = ({ children }) => (
  <div className="px-5 py-10 text-center text-sm text-slate-500">{children}</div>
);

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between">
    <dt className="text-slate-500">{label}</dt>
    <dd className="font-mono-alt tabular-nums text-slate-900">{value}</dd>
  </div>
);

function FinancePulseCard({ pulse }) {
  const closingPositive = (pulse.projected_closing ?? 0) >= 0;
  const netPositive = (pulse.net_month ?? 0) >= 0;
  const donorOverdue = pulse.overdue_donor_reports?.count || 0;
  const banksOpen = pulse.unreconciled_banks?.count || 0;
  return (
    <section className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="finance-pulse-section">
      {/* Projected closing */}
      <div className="dc-card overflow-hidden" data-testid="pulse-projected-closing">
        <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
          <Pulse size={16} weight="fill" style={{ color: "#1E5CBA" }} />
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Projected Closing · {pulse.current_period}</h3>
          <Link to="/finance?tab=cashflow" className="ml-auto dc-card-link">cashflow →</Link>
        </div>
        <div className="p-5 space-y-2">
          <div className={`font-display text-3xl font-bold tabular-nums ${closingPositive ? "text-slate-900" : "text-red-700"}`}>
            {formatCurrency(pulse.projected_closing)}
          </div>
          <dl className="space-y-1 text-sm">
            <Row label="Opening balance" value={formatCurrency(pulse.opening_balance)} />
            <div className="flex items-baseline justify-between">
              <dt className="text-slate-500">Net this month</dt>
              <dd className={`font-mono-alt tabular-nums font-semibold ${netPositive ? "text-emerald-700" : "text-red-600"}`}>
                {netPositive ? "+" : ""}{formatCurrency(pulse.net_month)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Overdue donor reports */}
      <div className="dc-card overflow-hidden" data-testid="pulse-overdue-donors">
        <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
          <Warning size={16} weight="fill" className={donorOverdue > 0 ? "text-red-600" : "text-emerald-600"} />
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Overdue Donor Reports</h3>
          <Link to="/finance?tab=donor" className="ml-auto dc-card-link">donors →</Link>
        </div>
        <div className="p-5">
          <div className={`font-display text-3xl font-bold tabular-nums ${donorOverdue > 0 ? "text-red-700" : "text-emerald-700"}`}>
            {donorOverdue}
          </div>
          {donorOverdue === 0 ? (
            <p className="mt-2 text-xs text-slate-500">All donor reports on schedule.</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {pulse.overdue_donor_reports.items.map((d) => (
                <li key={d.donor_key} className="flex items-center justify-between gap-2 text-xs" data-testid={`pulse-donor-${d.donor_key}`}>
                  <span className="truncate font-medium text-slate-900">{d.name}</span>
                  <span className="font-mono-alt text-red-600">{d.days_overdue}d overdue</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Unreconciled banks */}
      <div className="dc-card overflow-hidden" data-testid="pulse-unreconciled-banks">
        <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
          <ShieldWarning size={16} weight="fill" className={banksOpen > 0 ? "text-amber-600" : "text-emerald-600"} />
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Unreconciled Banks · {pulse.previous_period}</h3>
          <Link to="/finance?tab=bank_recon" className="ml-auto dc-card-link">reconcile →</Link>
        </div>
        <div className="p-5">
          <div className={`font-display text-3xl font-bold tabular-nums ${banksOpen > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            {banksOpen}<span className="ml-1 text-base font-normal text-slate-400">/ 4</span>
          </div>
          {banksOpen === 0 ? (
            <p className="mt-2 text-xs text-slate-500">All banks reconciled & approved.</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {pulse.unreconciled_banks.items.map((b) => (
                <li key={b.bank_key} className="flex items-center justify-between gap-2 text-xs" data-testid={`pulse-bank-${b.bank_key}`}>
                  <span className="truncate font-medium text-slate-900">{b.name}</span>
                  <span className="font-mono-alt uppercase tracking-wider text-amber-700">{b.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
